import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionLabel from "../components/SectionLabel";
import BackLink from "../components/BackLink";
import SEO from "../components/SEO";
import Picture from "../components/Picture";
import CTABand from "../components/sections/CTABand";
import {
    getInsightBySlug,
    getRelatedInsights,
    INSIGHT_CATEGORIES,
} from "../data/insights";

export default function InsightDetail() {
    const { slug } = useParams();
    const article = getInsightBySlug(slug);
    if (!article) return <Navigate to="/insights" replace />;
    const related = getRelatedInsights(slug, 2);

    return (
        <div data-testid={`insight-detail-${article.slug}`}>
            <SEO
                title={`${article.title} — ACT Insights`}
                description={article.excerpt}
                type="article"
                canonical={`https://actluxe.net/insights/${article.slug}`}
                lcpSlug={article.cover}
                lcpSizes="(max-width: 1280px) 100vw, 1280px"
                bare
            />
            {/* Article header */}
            <section className="relative bg-[#0B0B0C] pt-20 md:pt-28 pb-10 md:pb-16 overflow-hidden">
                <div className="shell">
                    <div className="max-w-3xl mx-auto">
                        <div className="mb-6">
                            <BackLink to="/insights" label="All insights" />
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mb-8">
                            <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#C9A86A]">
                                {INSIGHT_CATEGORIES[article.category] ||
                                    article.category}
                            </span>
                            <span className="h-px w-8 bg-[#C9A86A]/40" />
                            <span className="text-[10.5px] tracking-[0.28em] uppercase text-[#9A958A]">
                                {article.readTime}
                            </span>
                        </div>

                        <motion.h1
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.9,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="font-serif text-4xl md:text-5xl lg:text-[64px] leading-[1.08] text-[#F3EFE7]"
                        >
                            {article.title}
                        </motion.h1>

                        <div className="mt-10 flex items-center justify-between border-t border-[rgba(201,168,106,0.2)] pt-6">
                            <p className="text-[#9A958A] text-sm">
                                <span className="text-[#F3EFE7]">
                                    {article.author}
                                </span>{" "}
                                · {article.date}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Hero image */}
            <section className="bg-[#0B0B0C] overflow-hidden">
                <div className="shell">
                    <div className="relative overflow-hidden aspect-[16/9] [&>picture]:absolute [&>picture]:inset-0 [&>picture]:block [&>picture]:h-full [&>picture]:w-full">
                        <Picture
                            slug={article.cover}
                            alt={article.title}
                            priority
                            sizes="(max-width: 1280px) 100vw, 1280px"
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Body */}
            <section className="bg-[#0B0B0C] overflow-hidden">
                <div className="shell py-16 md:py-24">
                    <div className="max-w-2xl mx-auto">
                        {article.body.map((para, idx) => (
                            <p
                                key={`${article.slug}-para-${para.slice(0, 40)}`}
                                className={`text-[#F3EFE7] text-lg md:text-[19px] leading-[1.85] ${
                                    idx === 0
                                        ? "first-letter:font-serif first-letter:text-[#C9A86A] first-letter:text-6xl first-letter:leading-[0.85] first-letter:float-left first-letter:mr-3 first-letter:mt-2"
                                        : "mt-7"
                                }`}
                            >
                                {para}
                            </p>
                        ))}

                        {/* Decorative pullquote — the third paragraph echoed as a visual anchor */}
                        {article.body[2] && (
                            <motion.blockquote
                                suppressHydrationWarning
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.9,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="mt-12 border-l-2 border-[#C9A86A] pl-6 md:pl-8 font-serif italic text-2xl md:text-3xl text-[#C9A86A] leading-[1.35]"
                            >
                                {article.body[2].split(". ")[0]}.
                            </motion.blockquote>
                        )}
                    </div>
                </div>
            </section>

            {/* More from the Journal */}
            <section className="section-pad bg-[#15161A] border-t border-[rgba(201,168,106,0.12)] overflow-hidden">
                <div className="shell">
                    <SectionLabel number="—" label="More from the Journal" />
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-[rgba(201,168,106,0.15)] border border-[rgba(201,168,106,0.15)]">
                        {related.map((a, i) => (
                            <Link
                                key={a.slug}
                                to={`/insights/${a.slug}`}
                                className="group bg-[#15161A] p-10 md:p-12 transition-colors duration-500 hover:bg-[#1C1D22]"
                                data-testid={`insight-related-${a.slug}`}
                            >
                                <div className="flex items-center justify-between mb-8">
                                    <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#C9A86A]">
                                        {INSIGHT_CATEGORIES[a.category] ||
                                            a.category}
                                    </span>
                                    <span className="text-[10.5px] tracking-[0.28em] uppercase text-[#9A958A]">
                                        {a.date}
                                    </span>
                                </div>
                                <h3 className="font-serif text-2xl md:text-3xl text-[#F3EFE7] leading-[1.18] transition-colors duration-500 group-hover:text-[#D9BC80]">
                                    {a.title}
                                </h3>
                                <p className="mt-5 text-[#9A958A] leading-[1.85] line-clamp-3">
                                    {a.excerpt}
                                </p>
                                <span className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#C9A86A]">
                                    Read article
                                    <ArrowUpRight
                                        size={14}
                                        strokeWidth={1.2}
                                    />
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <CTABand />
        </div>
    );
}
